import { CircleAlert, CircleCheck, Copy, X } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { errorMessage, requestIdOf } from '../api/errors';
import { useI18n } from '../i18n/I18nProvider';
import { IconButton } from './Button';
import styles from './Feedback.module.css';

interface ToastItem {
  id: number;
  tone: 'success' | 'error';
  message: string;
  requestId: string | undefined;
}

interface ToastApi {
  success: (message: string) => void;
  /** Shows the localized message for an API error, with its request id for support. */
  error: (error: unknown, fallback?: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const SUCCESS_TIMEOUT_MS = 6000;

/**
 * Toasts live in two always-present live regions: successes are announced politely, errors
 * assertively. Errors stay until dismissed (they carry a reference the user may need to copy).
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((list) => list.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback((toast: Omit<ToastItem, 'id'>) => {
    const id = nextId.current++;
    setToasts((list) => [...list.slice(-3), { ...toast, id }]);
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      success: (message) => push({ tone: 'success', message, requestId: undefined }),
      error: (error, fallback) =>
        push({
          tone: 'error',
          message: fallback ?? errorMessage(t, error),
          requestId: requestIdOf(error),
        }),
    }),
    [push, t],
  );

  const successes = toasts.filter((toast) => toast.tone === 'success');
  const errors = toasts.filter((toast) => toast.tone === 'error');

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className={styles.toastRegion}>
        <div role="status" aria-live="polite" className={styles.toastStack}>
          {successes.map((toast) => (
            <ToastView key={toast.id} toast={toast} onDismiss={dismiss} autoDismiss />
          ))}
        </div>
        <div role="alert" aria-live="assertive" className={styles.toastStack}>
          {errors.map((toast) => (
            <ToastView key={toast.id} toast={toast} onDismiss={dismiss} />
          ))}
        </div>
      </div>
    </ToastContext.Provider>
  );
}

function ToastView({
  toast,
  onDismiss,
  autoDismiss,
}: {
  toast: ToastItem;
  onDismiss: (id: number) => void;
  autoDismiss?: boolean;
}) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!autoDismiss) return undefined;
    const timer = window.setTimeout(() => onDismiss(toast.id), SUCCESS_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [autoDismiss, onDismiss, toast.id]);

  return (
    <div className={styles.toast} data-tone={toast.tone} data-testid={`toast-${toast.tone}`}>
      {toast.tone === 'error' ? (
        <CircleAlert size={18} aria-hidden="true" />
      ) : (
        <CircleCheck size={18} aria-hidden="true" />
      )}
      <div className={styles.toastBody}>
        <p className={styles.toastTitle}>{toast.message}</p>
        {toast.requestId ? (
          <p className={styles.toastMeta}>
            <span>{t.errors.reference}</span>
            <code>{toast.requestId}</code>
            <IconButton
              size="sm"
              label={copied ? t.common.copied : t.errors.copyReference}
              icon={<Copy size={14} />}
              onPress={() => {
                const id = toast.requestId ?? '';
                navigator.clipboard
                  ?.writeText(id)
                  .then(() => setCopied(true))
                  .catch(() => setCopied(false));
              }}
            />
          </p>
        ) : null}
      </div>
      <IconButton
        size="sm"
        label={t.common.dismiss}
        icon={<X size={16} />}
        onPress={() => onDismiss(toast.id)}
      />
    </div>
  );
}

export function useToast(): ToastApi {
  const value = useContext(ToastContext);
  if (!value) throw new Error('useToast() must be used inside <ToastProvider>');
  return value;
}
